/* eslint-disable triple-rn-a11y/accessibility-role */
import { useQueryClient } from "@tanstack/react-query";
import { View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Text from "$src/core/components/atoms/Text/Text";
import { APP_BUILD_NUMBER, APP_VERSION } from "$src/core/constants/env";
import useAuth from "$src/core/hooks/use-auth";
import useConfig from "$src/core/hooks/use-config";
import useTranslation from "$src/core/hooks/use-translation";
import Alerter from "$src/core/services/alerting";
import Logging from "$src/core/services/logging/logging";
import SearchClassification from "$src/core/services/search-classification";
import AppleIntelligence from "$src/core/services/search-classification/apple-intelligence";
import ExecutorchEmbedding from "$src/core/services/search-classification/executorch-embedding";
import ExecutorchLlm from "$src/core/services/search-classification/executorch-llm";
import GoogleGeminiNano from "$src/core/services/search-classification/google-gemini-nano";
import SecureStorage from "$src/core/services/secure-storage";
import Storage from "$src/core/services/storage/storage";
import getDeviceTimeZone from "$src/core/utils/date/get-device-time-zone";

import $ from "./Debug.styles";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <View style={$.section}>
    <Text preset="heading4" accessibilityRole="header">
      {title}
    </Text>
    {children}
  </View>
);

const Row: React.FC<{
  label: string;
  value?: string | null;
  /** Renders the label as a tree branch; "end" draws the last one. */
  nested?: "branch" | "end";
}> = ({ label, value, nested }) => (
  <View style={$.row}>
    <Text preset="tiny" style={$.rowLabel}>
      {nested ? `${nested === "end" ? "└─" : "├─"} ${label}` : label}
    </Text>
    <Text preset="tiny" style={$.rowValue}>
      {value ?? "—"}
    </Text>
  </View>
);

const Debug: React.FC = () => {
  const config = useConfig();
  const { locale } = useTranslation();
  const { status: authStatus, account, profile } = useAuth();
  const queryClient = useQueryClient();

  const deviceTimeZone = getDeviceTimeZone();
  const timeZoneMode =
    config.intl.timeZone.type === "fixed"
      ? `locked (${config.intl.timeZone.value})`
      : "device";

  const handleShowAlert = (type: "success" | "error") => {
    switch (type) {
      case "success":
        Alerter.success("Debug: Success Alert");
        break;
      case "error":
        Alerter.error("Debug: Error Alert");
        break;
      default:
        throw new Error(`Invalid alert type: ${type as string}`);
    }
  };

  const handleClearStorage = () => {
    Storage.clear();
    Alerter.success("MMKV storage cleared");
  };

  const handleClearSecureStorage = async () => {
    try {
      await SecureStorage.clear();
      Alerter.success("Secure storage cleared");
    } catch (error) {
      Logging.error("Failed to clear secure storage", { error });
      Alerter.error("Failed to clear secure storage");
    }
  };

  const handleClearAllStorages = async () => {
    try {
      Storage.clear();
      await SecureStorage.clear();
      Alerter.success("All storages cleared");
    } catch (error) {
      Logging.error("Failed to clear all storages", { error });
      Alerter.error("Failed to clear all storages");
    }
  };

  const handleClearQueryCache = () => {
    queryClient.clear();
    Alerter.success("Query cache cleared");
  };

  const handleDeleteSearchModel = async () => {
    try {
      await Promise.all([
        ExecutorchEmbedding.deleteDownload(),
        ExecutorchLlm.deleteDownload(),
      ]);
      Alerter.success("Search model deleted");
    } catch (error) {
      Logging.error("Failed to delete search model", { error });
      Alerter.error("Failed to delete search model");
    }
  };

  return (
    <View style={$.container}>
      <Section title="App">
        <View style={$.rows}>
          <Row label="Flavor" value={config.name} />
          <Row label="Version" value={APP_VERSION} />
          <Row label="Build" value={APP_BUILD_NUMBER} />
          <Row label="Time mode" value={timeZoneMode} />
        </View>
      </Section>

      <Section title="Device">
        <View style={$.rows}>
          <Row label="Locale" value={locale} />
          <Row label="Time zone" value={deviceTimeZone} />
        </View>
      </Section>

      <Section title="Session">
        <View style={$.rows}>
          <Row label="Status" value={authStatus} />
          <Row label="Account" value={account?.email} />
          <Row label="Account ID" value={account?.id} />
          <Row label="Profile" value={profile?.name} />
        </View>
      </Section>

      <Section title="Search">
        <View style={$.rows}>
          <Row label="Classification Mode" value={SearchClassification.mode} />
          <Row
            nested="branch"
            label="Apple Intelligence"
            value={AppleIntelligence.status}
          />
          <Row
            nested="branch"
            label="Google Gemini Nano"
            value={GoogleGeminiNano.status}
          />
          <Row
            nested="branch"
            label="ExecuTorch embedding"
            value={ExecutorchEmbedding.status}
          />
          <Row
            nested="end"
            label="ExecuTorch LLM"
            value={ExecutorchLlm.status}
          />
        </View>
        <View style={$.actions}>
          <Button
            name="debug_delete_search_model"
            preset="secondary"
            onPress={handleDeleteSearchModel}
            label="Delete on-device search model"
          />
        </View>
      </Section>

      <Section title="Storage & cache">
        <View style={$.actions}>
          <Button
            name="debug_clear_storage"
            preset="secondary"
            onPress={handleClearStorage}
            label="Clear MMKV storage"
          />
          <Button
            name="debug_clear_secure_storage"
            preset="secondary"
            onPress={handleClearSecureStorage}
            label="Clear secure storage"
          />
          <Button
            name="debug_clear_all_storages"
            preset="secondary"
            onPress={handleClearAllStorages}
            label="Clear all storages"
          />
          <Button
            name="debug_clear_query_cache"
            preset="secondary"
            onPress={handleClearQueryCache}
            label="Clear query cache"
          />
        </View>
      </Section>

      <Section title="Alerts">
        <View style={$.actions}>
          <Button
            name="debug_show_success_alert"
            preset="secondary"
            onPress={() => handleShowAlert("success")}
            label="Show success alert"
          />
          <Button
            name="debug_show_error_alert"
            preset="secondary"
            onPress={() => handleShowAlert("error")}
            label="Show error alert"
          />
        </View>
      </Section>
    </View>
  );
};

export default Debug;
